// Ingestion Hub - 6-Group Categorized Document Upload
// Pursuit-specific knowledge base management with "Cabinet" metaphor

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRiskAssessment } from '../../contexts/RiskAssessmentContext';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText,
  Lightbulb,
  Award,
  Users,
  Trophy,
  FolderOpen,
  Upload,
  Check,
  X,
  Loader2,
  Brain,
  ArrowLeft,
  Trash2,
  RefreshCw,
  Sparkles,
  FileCheck,
  AlertCircle,
  Search,
  Zap,
  Calendar,
  Link as LinkIcon,
  FileStack,
  Plus,
  AlertTriangle,
  Building2,
  Crown,
  ShieldAlert,
  Medal,
  Library,
  FileSearch2
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  PullFromLibraryModal,
  PromoteToLibraryModal,
} from '../../components/pursuit/ClientLibraryLinkage';
import LibraryRecommendationsBanner from '../../components/pursuit/LibraryRecommendationsBanner';
import PursuitNextStepFooter from '../../components/pursuit/PursuitNextStepFooter';
import PursuitLifecycleStepper from '../../components/pursuit/PursuitLifecycleStepper';
import ComplianceMatrix from '../../components/pursuit/ComplianceMatrix';
import TeamingPartnerLibrary from '../../components/pursuit/TeamingPartnerLibrary';
import SectionMFactorsCard from '../../components/strategy/SectionMFactorsCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Document group configurations
const DOCUMENT_GROUPS = [
  {
    id: 1,
    name: "RFP & Solicitations",
    description: "RFP, SOW, Attachments, Amendments",
    icon: FileText,
    color: "blue",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    iconColor: "text-blue-600",
    priority: "Required",
    aiUsage: "Primary source for requirement extraction"
  },
  {
    id: 2,
    name: "Capability & Strategy",
    description: "CONOPS, Unique Approach, Capabilities",
    icon: Lightbulb,
    color: "purple",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    iconColor: "text-purple-600",
    priority: "Recommended",
    aiUsage: "Shapes win themes and technical approach"
  },
  {
    id: 3,
    name: "Past Performance",
    description: "Prime and Subcontractor records",
    icon: Award,
    color: "amber",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    iconColor: "text-amber-600",
    priority: "Recommended",
    aiUsage: "Validates team qualifications"
  },
  {
    id: 4,
    name: "Key Personnel",
    description: "Resumes, Bio-sketches",
    icon: Users,
    color: "green",
    bgColor: "bg-green-50",
    borderColor: "border-green-200",
    iconColor: "text-green-600",
    priority: "Recommended",
    aiUsage: "Matches staff to requirements"
  },
  {
    id: 5,
    name: "Winning Proposals",
    description: "Historical proposals (>30% similar)",
    icon: Trophy,
    color: "yellow",
    bgColor: "bg-yellow-50",
    borderColor: "border-yellow-200",
    iconColor: "text-yellow-600",
    priority: "High Value",
    aiUsage: "Ghost text and proven win themes"
  },
  {
    id: 6,
    name: "Other References",
    description: "Miscellaneous references and materials",
    icon: FolderOpen,
    color: "slate",
    bgColor: "bg-slate-50",
    borderColor: "border-slate-200",
    iconColor: "text-slate-600",
    priority: "Optional",
    aiUsage: "Supporting context"
  }
];

// Document Group Card with Drop Zone
function DocumentGroupCard({ group, documents, onUpload, onDelete, onProcess, onClearGroup, onToggleGoldSource, onPromoteToLibrary, isUploading }) {
  const [isDragActive, setIsDragActive] = useState(false);
  const GroupIcon = group.icon;
  
  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach(file => {
      onUpload(group.id, file);
    });
  }, [group.id, onUpload]);
  
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    }
  });
  
  const groupDocs = documents.filter(d => d.group_id === group.id);
  const hasDocuments = groupDocs.length > 0;
  const processedCount = groupDocs.filter(d => d.processed).length;
  const allProcessed = hasDocuments && processedCount === groupDocs.length;
  
  return (
    <Card 
      className={`relative overflow-hidden transition-all duration-300 ${
        isDragActive ? 'ring-2 ring-teal-500 scale-[1.02]' : ''
      } ${hasDocuments ? group.borderColor + ' border-2' : 'border border-slate-200'}`}
      data-testid={`group-card-${group.id}`}
    >
      {/* Cabinet Fill Indicator */}
      {hasDocuments && (
        <div className={`absolute top-0 left-0 right-0 h-1 ${
          allProcessed ? 'bg-emerald-500' : 'bg-amber-500'
        }`} />
      )}
      
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg ${group.bgColor} flex items-center justify-center flex-shrink-0`}>
            <GroupIcon className={`w-4 h-4 ${group.iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm font-semibold">{group.name}</CardTitle>
            <p className="text-xs text-slate-500">{group.description}</p>
          </div>
        </div>
        {/* Priority tag + Clear button at top right */}
        <div className="flex items-center gap-1.5 mt-2">
          <Badge 
            variant={group.priority === 'Required' ? 'default' : 'outline'}
            className={`text-[10px] ${group.priority === 'Required' ? 'bg-red-100 text-red-700 border-red-200' : ''}`}
          >
            {group.priority}
          </Badge>
          {hasDocuments && onClearGroup && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-slate-400 hover:text-red-600 ml-auto"
              onClick={(e) => { e.stopPropagation(); onClearGroup(group.id); }}
              title={`Clear all ${group.name} documents`}
              data-testid={`clear-group-${group.id}-btn`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* AI Usage Info */}
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Sparkles className="w-3 h-3" />
          <span>{group.aiUsage}</span>
        </div>
        
        {/* Drop Zone */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
            isDragActive 
              ? 'border-teal-500 bg-teal-50' 
              : hasDocuments
                ? `${group.borderColor} ${group.bgColor}`
                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
          }`}
        >
          <input {...getInputProps()} />
          {isUploading ? (
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
              <span className="text-sm text-slate-600">Uploading...</span>
            </div>
          ) : (
            <>
              <Upload className={`w-6 h-6 mx-auto mb-2 ${hasDocuments ? group.iconColor : 'text-slate-400'}`} />
              <p className="text-sm text-slate-600">
                {hasDocuments 
                  ? `${groupDocs.length} file${groupDocs.length > 1 ? 's' : ''} • Drop more to add`
                  : 'Drop files here or click to browse'
                }
              </p>
              <p className="text-xs text-slate-400 mt-1">PDF, DOCX, TXT (max 50MB)</p>
            </>
          )}
        </div>
        
        {/* Document List */}
        {groupDocs.length > 0 && (
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {groupDocs.map((doc) => {
              const isGoldSource = doc.persistence_level === 'GOLD_SOURCE';
              const isLink = doc.file_type === 'link';
              return (
                <div
                  key={doc.id}
                  className={`flex items-center justify-between p-2 border rounded-md text-sm ${
                    isGoldSource ? 'bg-amber-50 border-amber-200' : 'bg-white'
                  }`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isLink ? (
                      <LinkIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    ) : doc.processed ? (
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    ) : doc.extraction_status === 'failed' ? (
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    ) : (
                      <FileCheck className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    )}
                    {isLink && doc.link_url ? (
                      <a
                        href={doc.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="truncate text-blue-600 hover:underline"
                        title={doc.link_url}
                      >
                        {doc.file_name}
                      </a>
                    ) : (
                      <span className="truncate">{doc.file_name}</span>
                    )}
                    {isGoldSource && (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px] px-1.5 py-0">
                        GOLD
                      </Badge>
                    )}
                    {isLink && (
                      <Badge className="bg-blue-50 text-blue-600 border-blue-200 text-[10px] px-1.5 py-0">
                        LINK
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Gold Source Toggle — not applicable to links */}
                    {!isLink && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 w-7 p-0 ${isGoldSource ? 'text-amber-500 hover:text-amber-700' : 'text-slate-300 hover:text-amber-500'}`}
                        onClick={() => onToggleGoldSource?.(doc.id)}
                        title={isGoldSource ? 'Remove from Gold Source (will be auto-purged)' : 'Promote to Gold Source (exempt from auto-purge)'}
                        data-testid={`gold-source-toggle-${doc.id}`}
                      >
                        <Medal className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {!isLink && !doc.processed && doc.extraction_status !== 'failed' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => onProcess(doc.id)}
                        title="Process document"
                      >
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                    )}
                    {/* v3.12.0 — Save this doc to Client Library */}
                    {!isLink && onPromoteToLibrary && doc.file_size > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 w-7 p-0 ${
                          doc.promoted_to_kb
                            ? 'text-blue-600'
                            : 'text-slate-400 hover:text-blue-600'
                        }`}
                        onClick={() => onPromoteToLibrary(doc, group.id)}
                        title={
                          doc.promoted_to_kb
                            ? 'Already saved to Client Library — click to save again'
                            : 'Save to Client Library'
                        }
                        data-testid={`promote-to-library-${doc.id}`}
                      >
                        <Library className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                      onClick={() => onDelete(doc.id)}
                      title="Delete document"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Pursuit Brain Widget
function PursuitBrainWidget({ brainData, pursuit, analysisResults }) {
  if (!brainData) return null;
  
  const groupsWithDocs = Object.entries(brainData.groups || {});
  
  // Check if analysis was completed
  const analysisComplete = pursuit?.ai_analysis_completed || analysisResults?.success;
  const naicsVerified = pursuit?.naics_compliance_status === 'green' || pursuit?.naics_compliance_status === 'yellow' ||
                        analysisResults?.naics_compliance === 'green' || analysisResults?.naics_compliance === 'yellow';
  const pscVerified = pursuit?.psc_compliance_status === 'green' || pursuit?.psc_compliance_status === 'yellow' ||
                      analysisResults?.psc_compliance === 'green' || analysisResults?.psc_compliance === 'yellow';
  const similarityMatches = pursuit?.similarity_matches_count || analysisResults?.similarity_matches || brainData.similarity_matches_count || 0;
  const competitiveScore = pursuit?.competitive_alignment_score || analysisResults?.competitive_alignment_score;
  
  return (
    <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
            <Brain className="w-4 h-4 text-teal-600" />
          </div>
          <div>
            <CardTitle className="text-sm font-semibold">Pursuit Brain</CardTitle>
            <p className="text-xs text-slate-500">What AI knows about this pursuit</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {brainData.total_documents === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">
            No documents processed yet. Upload files to build the pursuit knowledge base.
          </p>
        ) : (
          <>
            {/* Prime Bidder - Prominent display at top */}
            {brainData.prime_bidder?.entity_name && (
              <div className="p-2 bg-amber-50 rounded-lg border border-amber-200 mb-2">
                <div className="flex items-center gap-2 mb-1">
                  <Crown className="w-3.5 h-3.5 text-amber-600" />
                  <span className="text-xs font-semibold text-amber-800">Prime Bidder</span>
                  {brainData.prime_bidder.client_name && (
                    <Badge className="ml-auto text-[9px] px-1.5 py-0 bg-blue-100 text-blue-700">
                      Client: {brainData.prime_bidder.client_name}
                    </Badge>
                  )}
                  {brainData.prime_bidder.set_aside_compliance?.alert_level === 'red' && (
                    <ShieldAlert className="w-3.5 h-3.5 text-red-500 ml-auto" />
                  )}
                </div>
                <p className="text-sm font-medium text-slate-900">
                  {brainData.prime_bidder.entity_name}
                </p>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-600">
                  {brainData.prime_bidder.uei && (
                    <span>UEI: <span className="font-mono">{brainData.prime_bidder.uei}</span></span>
                  )}
                  {brainData.prime_bidder.socioeconomic_status && (
                    <Badge className={`text-[9px] px-1 py-0 ${
                      brainData.prime_bidder.set_aside_compliance?.compliant === false
                        ? 'bg-red-100 text-red-700'
                        : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {brainData.prime_bidder.socioeconomic_status}
                    </Badge>
                  )}
                </div>
                {brainData.prime_bidder.set_aside_compliance?.compliant === false && (
                  <div className="mt-1.5 p-1.5 bg-red-100 rounded text-[10px] text-red-700 flex items-start gap-1">
                    <ShieldAlert className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{brainData.prime_bidder.set_aside_compliance.message}</span>
                  </div>
                )}
              </div>
            )}

            {/* Agency/Bureau Display */}
            {brainData.agency?.code && (
              <div className="flex flex-wrap items-center gap-1.5 p-2 bg-blue-50 rounded-lg border border-blue-200 mb-2">
                <Badge className="text-[10px] px-1.5 py-0.5 bg-blue-600 text-white">
                  {brainData.agency.code}
                </Badge>
                <span className="text-xs text-blue-800 truncate">
                  {brainData.agency.name}
                </span>
                {brainData.agency.bureau_code && (
                  <>
                    <span className="text-blue-400">|</span>
                    <Badge className="text-[10px] px-1.5 py-0.5 bg-blue-500 text-white">
                      {brainData.agency.bureau_code}
                    </Badge>
                    <span className="text-xs text-blue-700 truncate">
                      {brainData.agency.bureau_name}
                    </span>
                  </>
                )}
                {brainData.agency.experience_bonus > 0 && (
                  <Badge className="ml-auto text-[9px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700">
                    +{brainData.agency.experience_bonus} exp
                  </Badge>
                )}
              </div>
            )}

            {/* Experience Bonus Explanation */}
            {brainData.agency?.experience_explanation && (
              <div className="p-2 bg-emerald-50 rounded-lg border border-emerald-200 text-xs text-emerald-700 mb-2">
                <span className="font-medium">Experience Bonus:</span> {brainData.agency.experience_explanation}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="p-2 bg-white rounded border">
                <p className="text-xs text-slate-500">Documents</p>
                <p className="font-semibold text-teal-700">{brainData.total_documents}</p>
              </div>
              <div className="p-2 bg-white rounded border">
                <p className="text-xs text-slate-500">Knowledge Chunks</p>
                <p className="font-semibold text-teal-700">{brainData.total_chunks}</p>
              </div>
            </div>
            
            <div className="space-y-1">
              {groupsWithDocs.map(([groupId, data]) => {
                const group = DOCUMENT_GROUPS.find(g => g.id === parseInt(groupId));
                if (!group) return null;
                const GroupIcon = group.icon;
                
                return (
                  <div key={groupId} className="flex items-center gap-2 text-xs">
                    <GroupIcon className={`w-3 h-3 ${group.iconColor}`} />
                    <span className="text-slate-600">{data.name}:</span>
                    <span className="font-medium">{data.document_count} files</span>
                  </div>
                );
              })}
            </div>
            
            {/* Status Indicators */}
            <div className="space-y-1.5 pt-2 border-t border-slate-100">
              {brainData.rfp_shredded && (
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span className="text-slate-600">RFP Shredded</span>
                </div>
              )}
              {brainData.similarity_logic_active && (
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  <span className="text-slate-600">Similarity Logic Active</span>
                  {similarityMatches > 0 && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-purple-50 text-purple-700">
                      {similarityMatches} matches
                    </Badge>
                  )}
                </div>
              )}
              {naicsVerified && (
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span className="text-slate-600">NAICS Verified</span>
                  {pursuit?.naics_code && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700">
                      {pursuit.naics_code}
                    </Badge>
                  )}
                </div>
              )}
              {pscVerified && (
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-slate-600">PSC Verified</span>
                  {pursuit?.psc_code && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700">
                      {pursuit.psc_code}
                    </Badge>
                  )}
                </div>
              )}
              
              {/* Teaming Partners Summary - With Entity Type Labels */}
              {brainData.teaming && brainData.teaming.partner_count > 0 && (
                <div className="pt-2 mt-2 border-t border-slate-200">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-600 font-medium flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      Teaming Partners
                    </span>
                    <div className="flex items-center gap-1">
                      {brainData.teaming.score_bonus > 0 && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700">
                          +{brainData.teaming.score_bonus} pts
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-teal-50 text-teal-700">
                        {brainData.teaming.total_workshare}% workshare
                      </Badge>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {brainData.teaming.partners.map((partner, idx) => (
                      <div key={partner.id || partner.cage_code || partner.duns || partner.name || `partner-${idx}`} className="flex items-center justify-between text-xs bg-slate-50 px-2 py-1 rounded">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-slate-700">
                            {partner.abbr || partner.name?.slice(0, 10)}
                          </span>
                          <Badge className={`text-[8px] px-1 py-0 ${
                            partner.entity_type === 'CLIENT_SUB' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-slate-200 text-slate-600'
                          }`}>
                            {partner.entity_type === 'CLIENT_SUB' ? 'Client Sub' : 'External'}
                          </Badge>
                        </div>
                        <span className="text-slate-500">
                          {partner.role?.slice(0, 15)}{partner.role?.length > 15 ? '...' : ''} • {partner.workshare}%
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Teaming Requirement Matches */}
                  {brainData.teaming.total_matches > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-100">
                      <div className="flex items-center justify-between text-[10px] mb-1">
                        <span className="text-emerald-600 font-medium">Mitigated Requirements</span>
                        <span className="text-emerald-600">{brainData.teaming.total_matches} matched</span>
                      </div>
                      <div className="space-y-1 max-h-24 overflow-y-auto">
                        {brainData.teaming.requirement_matches?.slice(0, 3).map((match, idx) => (
                          <div key={idx} className="text-[10px] bg-emerald-50 px-2 py-1 rounded text-emerald-700">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{match.partner_abbr}</span>
                              <span className="text-emerald-500">{match.category}</span>
                            </div>
                            <p className="text-emerald-600 truncate mt-0.5">
                              {match.requirement_text}
                            </p>
                          </div>
                        ))}
                        {brainData.teaming.total_matches > 3 && (
                          <p className="text-[10px] text-emerald-500 text-center">
                            +{brainData.teaming.total_matches - 3} more matches
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Competitive Alignment Score */}
            {competitiveScore !== undefined && competitiveScore !== null && (
              <div className="p-2 bg-gradient-to-r from-blue-50 to-blue-100 rounded-md border border-blue-200">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-blue-700">Competitive Alignment</span>
                  <span className="font-bold text-blue-800">{competitiveScore}/100</span>
                </div>
                <Progress value={competitiveScore} className="h-1.5 mt-1 [&>div]:bg-blue-500" />
              </div>
            )}
            
            {/* Analysis Complete Summary */}
            {analysisComplete && (
              <div className="p-2 bg-emerald-50 rounded-md border border-emerald-200 text-xs text-emerald-700">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  <span className="font-medium">Analysis Complete</span>
                </div>
                <p className="mt-1 text-emerald-600">
                  {similarityMatches} Similarity Matches
                  {naicsVerified && ' | NAICS Verified'}
                  {pscVerified && ' | PSC Verified'}
                  {brainData.teaming?.total_matches > 0 && ` | ${brainData.teaming.total_matches} Teaming Matches`}
                </p>
              </div>
            )}
            
            {brainData.ai_ready && !analysisComplete && (
              <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-md text-amber-700 text-xs">
                <Zap className="w-4 h-4" />
                <span>Ready for AI Analysis - Click "Run AI Analysis"</span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Semantic Search Widget
function SemanticSearchWidget({ pursuitId, authToken, brainData }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    setHasSearched(true);
    
    try {
      const response = await fetch(`${API_URL}/api/pursuit-kb/${pursuitId}/search`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: searchQuery,
          limit: 5
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
      } else {
        toast.error('Search failed');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  // Don't show if no documents
  if (!brainData || brainData.total_documents === 0) {
    return null;
  }
  
  return (
    <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Search className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <CardTitle className="text-sm font-semibold">This Pursuit's Documents — Semantic Search</CardTitle>
            <p className="text-xs text-slate-500">Searches only the RFP, amendments, and other files uploaded to this pursuit.</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            placeholder="Ask about this pursuit..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            className="text-sm"
            data-testid="kb-search-input"
          />
          <Button 
            size="sm" 
            onClick={handleSearch}
            disabled={isSearching || !searchQuery.trim()}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="kb-search-button"
          >
            {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
        
        {hasSearched && (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {searchResults.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-2">No matching results found</p>
            ) : (
              searchResults.map((result, idx) => (
                <div 
                  key={result.chunk_id}
                  className="p-2 bg-white rounded-lg border border-slate-100 hover:border-indigo-200 transition-colors"
                  data-testid={`search-result-${idx}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs">
                      {result.group_name}
                    </Badge>
                    <span className="text-xs text-slate-400">
                      {Math.round(result.score * 100)}% match
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 line-clamp-3">{result.content}</p>
                  <p className="text-xs text-slate-400 mt-1 truncate">
                    From: {result.document_name}
                  </p>
                </div>
              ))
            )}
          </div>
        )}
        
        {!hasSearched && (
          <p className="text-xs text-slate-500 text-center">
            Try: "What are the key requirements?" or "Show evaluation criteria"
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function IngestionHub() {
  const { pursuitId, workspaceId } = useParams();
  const navigate = useNavigate();
  const { authToken, getAuthHeaders } = useAuth();
  const { updatePursuit, updateScanningStatus, clearScanningStatus } = useRiskAssessment();
  
  const [loading, setLoading] = useState(true);
  const [pursuit, setPursuit] = useState(null);
  const [summary, setSummary] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [brainData, setBrainData] = useState(null);
  const [uploadingGroup, setUploadingGroup] = useState(null);
  
  // RFP Confirmation Modal removed — Go/No-Go auto-unlocks on Group 1 upload
  
  // ============================================================================
  // RUN AI ANALYSIS - Pursuit Intelligence Loop
  // ============================================================================
  const [isRunningAnalysis, setIsRunningAnalysis] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  
  // ============================================================================
  // PURGE OPERATIONS - Reset Pursuit & Clear Group
  // ============================================================================
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showClearGroupConfirm, setShowClearGroupConfirm] = useState(null); // group_id to clear
  const [isResetting, setIsResetting] = useState(false);
  const [isClearingGroup, setIsClearingGroup] = useState(false);
  
  // ============================================================================
  // CLIENT PAST PERFORMANCE PULL - Pull PP records from Client Profile into Group 3
  // ============================================================================
  const [showClientPPModal, setShowClientPPModal] = useState(false);
  const [clientPPRecords, setClientPPRecords] = useState([]);
  const [loadingClientPP, setLoadingClientPP] = useState(false);
  const [pullingPP, setPullingPP] = useState(null);

  // ============================================================================
  // v3.12.0 — 2-Way Linkage with Client Library (all 4 cards)
  // Replaces the legacy "Pull Client PP" single-purpose flow above.
  // ============================================================================
  const [showLibraryPullModal, setShowLibraryPullModal] = useState(false);
  const [promoteState, setPromoteState] = useState({ open: false, doc: null, defaultEntityType: 'past-performances' });

  // Map a pursuit document group to its natural KB card so the promote modal
  // pre-selects the right tab (group_id 3 → past-performances, etc.).
  const groupIdToEntityType = (groupId) => {
    switch (groupId) {
      case 3: return 'past-performances';
      case 4: return 'key-personnel';
      case 5: return 'winning-proposals';
      case 6: return 'certifications';
      default: return 'past-performances';
    }
  };

  const handleOpenPromoteModal = (doc, groupId) => {
    setPromoteState({
      open: true,
      doc,
      defaultEntityType: groupIdToEntityType(groupId),
    });
  };

  const handleFetchClientPP = async () => {
    if (!pursuit?.organization_id) {
      toast.error('No client organization linked to this pursuit');
      return;
    }
    setShowClientPPModal(true);
    setLoadingClientPP(true);
    try {
      const response = await fetch(`${API_URL}/api/knowledge-base/past-performances?organization_id=${pursuit.organization_id}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setClientPPRecords(data.items || data.past_performances || []);
      } else {
        setClientPPRecords([]);
      }
    } catch (error) {
      console.error('Failed to fetch client PP:', error);
      setClientPPRecords([]);
    } finally {
      setLoadingClientPP(false);
    }
  };

  const handlePullClientPP = async (pp) => {
    setPullingPP(pp.id);
    try {
      const response = await fetch(`${API_URL}/api/pursuit-kb/${pursuitId}/documents/reference`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'CLIENT_PP',
          group_id: 3,
          title: pp.contract_title || pp.title || 'Past Performance',
          description: `Client PP: ${pp.client_agency || ''} — ${pp.contract_number || ''} — $${(pp.contract_value || 0).toLocaleString()}`
        })
      });
      if (response.ok) {
        toast.success(`"${pp.contract_title || pp.title}" linked to Past Performance`);
        fetchData();
      } else {
        toast.error('Failed to pull past performance record');
      }
    } catch (error) {
      toast.error('Failed to pull past performance record');
    } finally {
      setPullingPP(null);
    }
  };

  // ============================================================================
  // SEMANTIC SEARCH — Folded from Pursuit Knowledge Base
  // ============================================================================
  const [showDocSearch, setShowDocSearch] = useState(false);
  const [docSearchQuery, setDocSearchQuery] = useState('');
  const [docSearchResults, setDocSearchResults] = useState([]);
  const [searchingDocs, setSearchingDocs] = useState(false);

  const handleDocSearch = async () => {
    if (!docSearchQuery.trim()) return;
    setSearchingDocs(true);
    try {
      const response = await fetch(`${API_URL}/api/pursuit-kb/${pursuitId}/search`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: docSearchQuery, top_k: 10 })
      });
      if (response.ok) {
        const data = await response.json();
        setDocSearchResults(data.results || []);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearchingDocs(false);
    }
  };
  
  // Reset Pursuit - Delete all documents and reset scores
  const handleResetPursuit = async () => {
    setIsResetting(true);
    try {
      const response = await fetch(`${API_URL}/api/purge/pursuit/${pursuitId}/reset`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Pursuit reset! ${result.documents_deleted} documents purged.`);
        setShowResetConfirm(false);
        fetchData(); // Refresh all data
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset pursuit');
      }
    } catch (error) {
      console.error('Reset error:', error);
      toast.error('Failed to reset pursuit');
    } finally {
      setIsResetting(false);
    }
  };
  
  // Clear Group - Delete all documents in a specific group
  const handleClearGroup = async (groupId) => {
    setIsClearingGroup(true);
    try {
      const response = await fetch(`${API_URL}/api/purge/pursuit/${pursuitId}/group/${groupId}/clear`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const result = await response.json();
        const groupName = DOCUMENT_GROUPS.find(g => g.id === groupId)?.name || `Group ${groupId}`;
        toast.success(`${groupName} cleared! ${result.documents_deleted} documents purged.`);
        setShowClearGroupConfirm(null);
        fetchData(); // Refresh all data
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clear group');
      }
    } catch (error) {
      console.error('Clear group error:', error);
      toast.error('Failed to clear group');
    } finally {
      setIsClearingGroup(false);
    }
  };
  
  const handleRunAnalysis = async () => {
    setIsRunningAnalysis(true);
    setAnalysisProgress({ stage: 'Starting', progress: 0, message: 'Initializing Pursuit Brain...' });
    
    try {
      // Show progressive stages
      const stages = [
        { stage: 'Vectorization', progress: 15, message: 'Processing documents into knowledge chunks...' },
        { stage: 'Extraction', progress: 35, message: 'Extracting NAICS/PSC codes from RFP...' },
        { stage: 'Compliance', progress: 55, message: 'Validating compliance requirements...' },
        { stage: 'Similarity', progress: 75, message: 'Finding matches with Winning Proposals...' },
        { stage: 'Scoring', progress: 90, message: 'Updating competitive alignment score...' }
      ];
      
      // Animate progress stages
      let currentStage = 0;
      const progressInterval = setInterval(() => {
        if (currentStage < stages.length) {
          setAnalysisProgress(stages[currentStage]);
          currentStage++;
        }
      }, 1500);
      
      // Call the API with abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 min client-side timeout
      
      const response = await fetch(`${API_URL}/api/pursuit-kb/${pursuitId}/run-analysis`, {
        method: 'POST',
        headers: getAuthHeaders(),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      clearInterval(progressInterval);
      
      if (response.ok) {
        const results = await response.json();
        setAnalysisProgress({ stage: 'Complete', progress: 100, message: 'Analysis complete!' });
        setAnalysisResults(results);
        
        // Show success toast with score
        const fitScore = results?.competitive_alignment_score || results?.summary?.competitive_alignment || 0;
        toast.success(`Analysis Complete! Fit Score: ${fitScore}/100`);
        
        // Refresh data
        fetchData();
        
        // Clear progress after delay
        setTimeout(() => {
          setAnalysisProgress(null);
        }, 3000);
      } else {
        let errorMsg = 'Analysis failed';
        let detailObj = null;
        try {
          const error = await response.json();
          if (typeof error?.detail === 'object' && error.detail !== null) {
            detailObj = error.detail;
            errorMsg = detailObj.message || errorMsg;
          } else if (typeof error?.detail === 'string') {
            errorMsg = error.detail;
          }
        } catch (e) {
          // Response body not parseable
        }
        // 400 with code=BYOM_NOT_CONFIGURED → prompt the org to set up
        // their own LLM key instead of a generic error. Mirrors the
        // BidPursuitScorecard flow.
        if (response.status === 400 && detailObj?.code === 'BYOM_NOT_CONFIGURED') {
          toast.error(
            'BYOM key required',
            {
              description: 'This organization has no BYOM (bring-your-own-model) LLM API key configured. Add one in Settings → AI Config to use AI features.',
              duration: 9000,
              action: {
                label: 'Configure BYOM',
                onClick: () => navigate(
                  workspaceId ? `/${workspaceId}/settings` : '/admin/dashboard'
                ),
              },
            }
          );
        } else {
          toast.error(errorMsg);
        }
        setAnalysisProgress(null);
      }
    } catch (error) {
      console.error('Analysis error:', error);
      if (error.name === 'AbortError') {
        toast.error('Analysis timed out — the RFP may be too large. Try processing fewer documents.');
      } else {
        toast.error('Analysis failed. Please try again.');
      }
      setAnalysisProgress(null);
    } finally {
      setIsRunningAnalysis(false);
    }
  };
  
  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      
      // Fetch pursuit details
      const pursuitRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, { headers });
      if (pursuitRes.ok) {
        const pursuitData = await pursuitRes.json();
        
        // Also fetch evaluation data to include in pursuit for HUD score calculation
        const evalRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}/evaluation`, { headers });
        if (evalRes.ok) {
          const evalData = await evalRes.json();
          pursuitData.evaluation = evalData;
        }
        
        setPursuit(pursuitData);
        // Update Risk Assessment HUD with pursuit data (now includes evaluation)
        updatePursuit(pursuitData);
      }
      
      // Fetch ingestion summary
      const summaryRes = await fetch(`${API_URL}/api/pursuit-kb/${pursuitId}/summary`, { headers });
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData);
      }
      
      // Fetch all documents
      const docsRes = await fetch(`${API_URL}/api/pursuit-kb/${pursuitId}/documents`, { headers });
      if (docsRes.ok) {
        const docsData = await docsRes.json();
        setDocuments(docsData.documents || []);
      }
      
      // Fetch brain data
      const brainRes = await fetch(`${API_URL}/api/pursuit-kb/${pursuitId}/brain`, { headers });
      if (brainRes.ok) {
        const brainResult = await brainRes.json();
        setBrainData(brainResult);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load ingestion data');
    } finally {
      setLoading(false);
    }
  };
  
  // Clear scanning status when leaving
  useEffect(() => {
    return () => {
      clearScanningStatus();
    };
  }, [clearScanningStatus]);
  
  useEffect(() => {
    if (pursuitId && authToken) {
      fetchData();
    }
  }, [pursuitId, authToken]);
  
  // Handle file upload
  const handleUpload = async (groupId, file) => {
    setUploadingGroup(groupId);
    // Update HUD scanning status
    updateScanningStatus(groupId, 'scanning');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('group_id', groupId);
      
      const response = await fetch(`${API_URL}/api/pursuit-kb/${pursuitId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        const docId = result.document?.id;
        toast.success(`Uploaded to ${DOCUMENT_GROUPS.find(g => g.id === groupId)?.name}`);
        
        // Auto-process the document to generate embeddings
        if (docId) {
          toast.info('Processing document for AI search...');
          try {
            const processResponse = await fetch(`${API_URL}/api/pursuit-kb/${pursuitId}/process/${docId}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`
              }
            });
            
            if (processResponse.ok) {
              const processResult = await processResponse.json();
              toast.success(`Document processed: ${processResult.chunks_created} knowledge chunks created`);
              
              // If this is an RFP document (Group 1), auto-unlock Go/No-Go
              if (groupId === 1) {
                try {
                  const unlockResponse = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, {
                    method: 'PATCH',
                    headers: {
                      ...getAuthHeaders(),
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      rfp_shredded: true,
                      go_nogo_unlocked: true
                    })
                  });
                  if (unlockResponse.ok) {
                    toast.success('RFP uploaded. Go/No-Go analysis unlocked.');
                  }
                } catch (unlockError) {
                  console.error('Go/No-Go unlock error:', unlockError);
                }
              }
            } else {
              toast.warning('Document uploaded but processing failed. Click refresh to retry.');
            }
          } catch (processError) {
            console.error('Processing error:', processError);
            toast.warning('Document uploaded but processing failed.');
          }
        }
        
        fetchData(); // Refresh all data
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    } finally {
      setUploadingGroup(null);
      // Clear scanning status for this group
      updateScanningStatus(groupId, 'complete');
    }
  };
  
  // Handle document deletion
  const handleDelete = async (documentId) => {
    if (!confirm('Delete this document?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/pursuit-kb/${pursuitId}/documents/${documentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Document deleted');
        fetchData();
      } else {
        toast.error('Failed to delete document');
      }
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };
  
  // Handle document processing
  const handleProcess = async (documentId) => {
    try {
      const response = await fetch(`${API_URL}/api/pursuit-kb/${pursuitId}/process/${documentId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Document processed');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Processing failed');
      }
    } catch (error) {
      toast.error('Processing failed');
    }
  };
  
  // Handle Gold Source toggle
  const handleToggleGoldSource = async (documentId) => {
    try {
      const response = await fetch(`${API_URL}/api/purge/document/${documentId}/toggle-gold-source`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.is_gold_source) {
          toast.success('Document promoted to Gold Source', {
            description: 'This document is now exempt from auto-purge'
          });
        } else {
          toast.info('Gold Source status removed', {
            description: 'Document will now be subject to auto-purge'
          });
        }
        fetchData();
      } else {
        toast.error('Failed to update document status');
      }
    } catch (error) {
      toast.error('Failed to update document status');
    }
  };
  
  // Calculate readiness
  const rfpGroup = summary?.groups?.find(g => g.group_id === 1);
  const isReadyForSuggest = rfpGroup?.document_count > 0;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="ingestion-hub">
      {/* Header — v3.18.24 canonical: px-8 py-4 + max-w-7xl mx-auto */}
      <div className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <Link to={`/${workspaceId}/pursuit/${pursuitId}/intake`} className="hover:text-teal-600 flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" />
                Back to Pursuit
              </Link>
            </div>
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Document Ingestion Hub</h1>
            <p className="text-sm text-slate-500 mt-1">
              {pursuit?.opportunity_name || 'Loading...'}
            </p>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          {/* Semantic Search across all documents */}
          <Button 
            variant="outline"
            onClick={() => setShowDocSearch(true)}
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="search-docs-btn"
          >
            <Search className="w-4 h-4 mr-2" />
            Search Documents
          </Button>
          
          {/* v3.12.0 — Pull from Client Library (all 4 cards) */}
          <Button 
            variant="outline"
            onClick={() => setShowLibraryPullModal(true)}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="pull-client-library-btn"
          >
            <Library className="w-4 h-4 mr-2" />
            Pull from Client Library
          </Button>
          
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {isReadyForSuggest && (
            <Button 
              className="bg-teal-600 hover:bg-teal-700"
              onClick={handleRunAnalysis}
              disabled={isRunningAnalysis}
              data-testid="run-analysis-btn"
            >
              {isRunningAnalysis ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Run AI Analysis
                </>
              )}
            </Button>
          )}
          
          {/* Reset Pursuit Button */}
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-500 hover:text-red-600 hover:bg-red-50"
            onClick={() => setShowResetConfirm(true)}
            title="Reset Pursuit - Delete all documents"
            data-testid="reset-pursuit-btn"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          </div>
        </div>
      </div>

      <div className="px-8">
        <div className="max-w-7xl mx-auto">
          <PursuitLifecycleStepper />
        </div>
      </div>

      <div className="p-8">
        <div className="max-w-7xl mx-auto space-y-6">
      
      {/* AI Analysis Progress Bar */}
      {analysisProgress && (
        <Card className="p-4 border-teal-200 bg-teal-50/50">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Brain className="w-5 h-5 text-teal-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-teal-800">
                  Brain Processing: {analysisProgress.stage}
                </span>
                <span className="text-sm text-teal-600">{analysisProgress.progress}%</span>
              </div>
              <p className="text-xs text-teal-600 mt-0.5">{analysisProgress.message}</p>
            </div>
          </div>
          <Progress 
            value={analysisProgress.progress} 
            className="h-2 [&>div]:bg-teal-500"
          />
        </Card>
      )}
      
      {/* Progress Bar */}
      {summary && !analysisProgress && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Ingestion Progress</span>
            <span className="text-sm text-slate-500">
              {summary.groups.filter(g => g.document_count > 0).length} of 6 groups populated
            </span>
          </div>
          <Progress 
            value={(summary.groups.filter(g => g.document_count > 0).length / 6) * 100} 
            className="h-2"
          />
          {!isReadyForSuggest && (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Upload RFP documents (Group 1) to enable AI analysis
            </p>
          )}
        </Card>
      )}

      {/* v3.12.1 — Library-driven AI recommendations banner */}
      <LibraryRecommendationsBanner
        pursuitId={pursuitId}
        authToken={authToken}
        onPulled={fetchData}
      />
      
      {/* Main Content */}
      <div className="grid grid-cols-12 gap-6">
        {/* Document Groups - 9 cols */}
        <div className="col-span-12 lg:col-span-9">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {DOCUMENT_GROUPS.map((group) => (
              <DocumentGroupCard
                key={group.id}
                group={group}
                documents={documents}
                onUpload={handleUpload}
                onDelete={handleDelete}
                onProcess={handleProcess}
                onClearGroup={(groupId) => setShowClearGroupConfirm(groupId)}
                onToggleGoldSource={handleToggleGoldSource}
                onPromoteToLibrary={handleOpenPromoteModal}
                isUploading={uploadingGroup === group.id}
              />
            ))}
          </div>
        </div>
        
        {/* Pursuit Brain Sidebar - 3 cols */}
        <div className="col-span-12 lg:col-span-3 space-y-4">
          <PursuitBrainWidget
            brainData={brainData}
            pursuit={pursuit}
            analysisResults={analysisResults}
          />

          {/* Semantic Search */}
          <SemanticSearchWidget
            pursuitId={pursuitId}
            authToken={authToken}
            brainData={brainData}
          />

          {/* Quick Stats */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Quick Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Total Documents</span>
                <span className="font-medium">{summary?.total_documents || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Knowledge Chunks</span>
                <span className="font-medium">{summary?.total_chunks || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Groups Populated</span>
                <span className="font-medium">
                  {summary?.groups?.filter(g => g.document_count > 0).length || 0} / 6
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Section M — Submittal Volumes (Section L) + Evaluation Factors.
          AI-extract from the RFP, review/confirm; feeds the Compliance Matrix
          build gate directly below. Moved here from Strategy Workspace so all
          three (Section M, Submittal Volumes, Compliance Matrix) sit together. */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-3">
          <FileSearch2 className="w-5 h-5 text-cyan-600" />
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Section M — Submittal Volumes &amp; Evaluation Factors
          </h2>
          <span className="text-xs text-slate-400 ml-auto italic">
            AI-extract from the RFP, then review and confirm. Weights drive every proposal volume.
          </span>
        </div>
        <SectionMFactorsCard pursuitId={pursuitId} />
      </div>

      {/* Capability / Requirements Matrix lives on the Pursuit Intake page
          (moved there with the requirement set it produces). */}

      {/* Compliance Matrix — requirement mapping + Gap Check, gated on the
          Section M extraction above. Full-width below the document grid. */}
      <div className="mt-6">
        <ComplianceMatrix pursuitId={pursuitId} token={authToken} />
      </div>

      {/* Teaming Partner Library — read-only capabilities, past performance &
          certifications for any client orgs linked as teaming partners on the
          Intake screen. Renders nothing when no partners are linked. */}
      <div className="mt-6">
        <TeamingPartnerLibrary pursuitId={pursuitId} authToken={authToken} />
      </div>

      {/* RFP Confirmation Modal removed — Go/No-Go auto-unlocks on Group 1 upload */}
      
      {/* Reset Pursuit Confirmation Modal */}
      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldAlert className="w-5 h-5" />
              Reset Pursuit - Destructive Action
            </DialogTitle>
            <DialogDescription>
              This will permanently delete:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-500" />
                All documents across all 6 groups
              </li>
              <li className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-500" />
                All knowledge base chunks and embeddings
              </li>
              <li className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-500" />
                Go/No-Go score and analysis results
              </li>
            </ul>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>This action cannot be undone.</strong> The deletion will be recorded in the audit log.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetConfirm(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleResetPursuit}
              disabled={isResetting}
              data-testid="confirm-reset-pursuit-btn"
            >
              {isResetting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Reset Pursuit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Clear Group Confirmation Modal */}
      <Dialog open={showClearGroupConfirm !== null} onOpenChange={() => setShowClearGroupConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Trash2 className="w-5 h-5" />
              Clear {DOCUMENT_GROUPS.find(g => g.id === showClearGroupConfirm)?.name || 'Group'}
            </DialogTitle>
            <DialogDescription>
              This will permanently delete all documents in this group.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600">
              {documents.filter(d => d.group_id === showClearGroupConfirm).length} document(s) will be removed.
              This action will be recorded in the audit log.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearGroupConfirm(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleClearGroup(showClearGroupConfirm)}
              disabled={isClearingGroup}
              data-testid="confirm-clear-group-btn"
            >
              {isClearingGroup ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Clear Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Semantic Search Dialog */}
      <Dialog open={showDocSearch} onOpenChange={setShowDocSearch}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-blue-600" />
              Search Pursuit Documents
            </DialogTitle>
            <DialogDescription>
              Ask natural language questions across all uploaded documents.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex items-center gap-2 py-4">
            <Input
              placeholder="e.g., What are the cybersecurity requirements?"
              value={docSearchQuery}
              onChange={(e) => setDocSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleDocSearch()}
              className="flex-1"
              data-testid="doc-search-input"
            />
            <Button 
              onClick={handleDocSearch}
              disabled={searchingDocs || !docSearchQuery.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="doc-search-btn"
            >
              {searchingDocs ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
            </Button>
          </div>
          
          <div className="flex-1 overflow-y-auto space-y-2 min-h-[200px]">
            {docSearchResults.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Search className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-sm">Search across all uploaded documents</p>
              </div>
            ) : (
              docSearchResults.map((result, idx) => (
                <div 
                  key={result.id || result.document_id || `${result.document_name || 'doc'}-${idx}`}
                  className="p-3 bg-slate-50 rounded-lg border border-slate-200"
                  data-testid={`search-result-${idx}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-slate-500" />
                    <span className="text-xs font-medium text-slate-700">{result.document_name || 'Document'}</span>
                    {result.score && (
                      <Badge variant="outline" className="text-[10px] px-1.5">{Math.round(result.score * 100)}% match</Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-3">{result.content || result.text}</p>
                </div>
              ))
            )}
          </div>
          
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => { setShowDocSearch(false); setDocSearchResults([]); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Past Performance Pull Dialog */}
      <Dialog open={showClientPPModal} onOpenChange={setShowClientPPModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-600" />
              Pull from Client Past Performance
            </DialogTitle>
            <DialogDescription>
              Link past performance records from the Client Profile into this pursuit's document library.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-2 min-h-[200px] py-4">
            {loadingClientPP ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : clientPPRecords.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Award className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-sm">No past performance records found for this client</p>
                <p className="text-xs mt-1">Add records in the Client Profile first</p>
              </div>
            ) : (
              clientPPRecords.map((pp) => (
                <div 
                  key={pp.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-amber-300 transition-colors"
                  data-testid={`pp-record-${pp.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{pp.contract_title || pp.title}</p>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
                      {pp.client_agency && <span>{pp.client_agency}</span>}
                      {pp.contract_number && <span>#{pp.contract_number}</span>}
                      {pp.contract_value && <span>${Number(pp.contract_value).toLocaleString()}</span>}
                      {pp.cpars_rating && (
                        <Badge variant="outline" className="text-[10px] px-1.5">{pp.cpars_rating}</Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handlePullClientPP(pp)}
                    disabled={pullingPP === pp.id}
                    className="bg-teal-600 hover:bg-teal-700 text-xs ml-3"
                    data-testid={`pull-pp-${pp.id}`}
                  >
                    {pullingPP === pp.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>
                        <Plus className="w-3 h-3 mr-1" />
                        Pull
                      </>
                    )}
                  </Button>
                </div>
              ))
            )}
          </div>
          
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setShowClientPPModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v3.12.0 — Unified Pull from Client Library (all 4 cards) */}
      <PullFromLibraryModal
        open={showLibraryPullModal}
        onOpenChange={setShowLibraryPullModal}
        pursuitId={pursuitId}
        organizationId={pursuit?.organization_id}
        authToken={authToken}
        onPulled={fetchData}
      />

      {/* v3.12.0 — Save (Push) a pursuit doc to Client Library */}
      <PromoteToLibraryModal
        open={promoteState.open}
        onOpenChange={(o) => setPromoteState({ ...promoteState, open: o })}
        pursuitId={pursuitId}
        pursuitDocId={promoteState.doc?.id}
        pursuitDocName={promoteState.doc?.file_name}
        organizationId={pursuit?.organization_id}
        authToken={authToken}
        defaultEntityType={promoteState.defaultEntityType}
        onPromoted={fetchData}
      />

      <PursuitNextStepFooter currentStep="ingestion" />
        </div>
      </div>
    </div>
  );
}
